import * as React from "react";
import { useEffect, useState } from "react";
import { apiConstants, get, post } from "../../common/constants";
import {
    Card,
    CardContent,
    Button,
    Chip,
    Box,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Typography,
    alpha,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import ProzoDataGrid from "../../common/ProzoDataGrid";
import { FormProvider } from "../../common/hook-form";
import { CourierFilter } from "../../common/ProshipFormComponents";
import { LoadingButton } from "@mui/lab";
import { useForm } from "react-hook-form";
import PageTitle from "../../components/PageTitle";
import MUIModal from "../../components/MuiModal";
import CourierUpload from "./CourierUpload";
import AddEmail from "./AddEmail";
import Loader from '../../common/Loader';
import AddKAM from "../B2BOrder/AddKam";
import { useSnackbar } from "notistack";
import AddIcon from '@mui/icons-material/Add';
import AddCourier from "./AddCourier";
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import SideDrawer from "../../components/SideDrawer";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import { FieldLabel, FilterField } from '../../common/FilterBar';

// Rich table cells — mirrors the D2C Forward Shipment table (UnprocessedOrdersForward.js).
const CellLine = ({ children, muted, bold, ellipsis }) => (
    <Box sx={{
        fontSize: 12.5, lineHeight: 1.4,
        color: muted ? 'text.secondary' : 'text.primary',
        fontWeight: bold ? 600 : 400,
        ...(ellipsis
            ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
            : { whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }),
    }}>{children}</Box>
);
const CellStack = ({ children }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35, width: '100%', py: 0.5 }}>{children}</Box>
);
const gridSx = {
    '& .MuiDataGrid-columnHeaders': { backgroundColor: '#F9FAFB', borderBottom: '1px solid #E2E8F0' },
    '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700, color: '#475569' },
    '& .MuiDataGrid-cell': { paddingTop: '12px', paddingBottom: '12px' },
};

function ActionMenu({ row, onEmail, onKam, onZone, onPricing, onRefresh, onEdit }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (e) => setAnchorEl(e.currentTarget);
    const handleClose = () => setAnchorEl(null);
    const menuAction = (fn) => () => { handleClose(); fn(); };

    return (
        <>
            <Button
                size="small"
                variant="contained"
                disableElevation
                onClick={handleClick}
                endIcon={<MoreVertIcon />}
                sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#e3f2fd', color: '#1565c0', boxShadow: 'none', '&:hover': { bgcolor: '#1565c0', color: '#fff', boxShadow: 'none' } }}
            >
                Actions
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                    elevation: 8,
                    sx: {
                        minWidth: 230,
                        borderRadius: 2,
                        mt: 0.5,
                        py: 0.5,
                        border: '1px solid',
                        borderColor: 'divider',
                    }
                }}
            >
                {/* Section: Configure */}
                <Typography variant="overline" sx={{ px: 2, pt: 0.5, pb: 0.5, display: 'block', color: 'text.secondary', letterSpacing: 1, fontSize: '0.65rem' }}>
                    Configure
                </Typography>
                <MenuItem onClick={menuAction(onEmail)} sx={{ mx: 1, borderRadius: 1, py: 0.8, '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08) } }}>
                    <ListItemIcon><EmailOutlinedIcon fontSize="small" sx={{ color: 'primary.main' }} /></ListItemIcon>
                    <ListItemText primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}>Add Pickup Email</ListItemText>
                </MenuItem>
                <MenuItem onClick={menuAction(onKam)} sx={{ mx: 1, borderRadius: 1, py: 0.8, '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08) } }}>
                    <ListItemIcon><PersonAddAltOutlinedIcon fontSize="small" sx={{ color: 'primary.main' }} /></ListItemIcon>
                    <ListItemText primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}>Add KAM Details</ListItemText>
                </MenuItem>

                <Divider sx={{ my: 0.5, mx: 1.5 }} />

                {/* Section: Upload */}
                <Typography variant="overline" sx={{ px: 2, pt: 0.5, pb: 0.5, display: 'block', color: 'text.secondary', letterSpacing: 1, fontSize: '0.65rem' }}>
                    Upload
                </Typography>
                <MenuItem onClick={menuAction(onZone)} sx={{ mx: 1, borderRadius: 1, py: 0.8, '&:hover': { bgcolor: (theme) => alpha(theme.palette.info.main, 0.08) } }}>
                    <ListItemIcon><MapOutlinedIcon fontSize="small" sx={{ color: 'info.main' }} /></ListItemIcon>
                    <ListItemText primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}>Zone Mapping</ListItemText>
                </MenuItem>
                <MenuItem onClick={menuAction(onPricing)} sx={{ mx: 1, borderRadius: 1, py: 0.8, '&:hover': { bgcolor: (theme) => alpha(theme.palette.info.main, 0.08) } }}>
                    <ListItemIcon><AttachMoneyOutlinedIcon fontSize="small" sx={{ color: 'info.main' }} /></ListItemIcon>
                    <ListItemText primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}>Pricing</ListItemText>
                </MenuItem>

                <Divider sx={{ my: 0.5, mx: 1.5 }} />

                {/* Section: Manage */}
                <MenuItem onClick={menuAction(onRefresh)} sx={{ mx: 1, borderRadius: 1, py: 0.8, '&:hover': { bgcolor: (theme) => alpha(theme.palette.warning.main, 0.08) } }}>
                    <ListItemIcon><RefreshIcon fontSize="small" sx={{ color: 'warning.main' }} /></ListItemIcon>
                    <ListItemText primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}>Refresh Config</ListItemText>
                </MenuItem>
                <MenuItem onClick={menuAction(onEdit)} sx={{ mx: 1, borderRadius: 1, py: 0.8, '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08) } }}>
                    <ListItemIcon><EditIcon fontSize="small" sx={{ color: 'primary.main' }} /></ListItemIcon>
                    <ListItemText primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}>Edit Courier</ListItemText>
                </MenuItem>
            </Menu>
        </>
    );
}


export default function B2BCouriers() {
    const { search } = useLocation();
    const queryParams = React.useMemo(
        () => new URLSearchParams(search),
        [search]
    );
    const { enqueueSnackbar } = useSnackbar();
    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(20);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isRefConfig, setRefConfig] = useState(false);
    const [filter, setFilter] = useState();
    const [courierFilter, setCourierFilter] = useState(null);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [fileType, setFileType] = useState("");
    const [url, setUrl] = useState("");
    const [reload, setReload] = useState(1);
    const [title, setTitle] = useState("");
    const [id, setId] = useState();
    const [form, setForm] = useState();
    const [sample, setSample] = useState("");
    const [tabList, setTabList] = useState([]);
    const [rowData, setRowData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [isEditData, setIsEditData] = useState(null);

    const methods = useForm({
        defaultValues: {
            courier: "",
        },
    });
    const { handleSubmit, reset } = methods;

    const onSubmit = async (data) => {
        setIsSubmitting(true);
        setFilter(data.courier);
        setIsSubmitting(false);
    };

    useEffect(() => {
        setIsLoading(true);
        let pageNo = parseInt(queryParams.get("page")) || page;
        let limit = parseInt(queryParams.get("limit")) || sizePerPage;
        if (filter) {
            get(`${apiConstants.COURIERS}/${filter}`).then((res) => {
                setCourierFilter([res?.data?.result]);
                setIsLoading(false);
            }).catch((err) => {
                console.log(err);
                setIsLoading(false);
            });
        } else {
            const andfilter = [
                { field: "supported_shipment", operator: "eq", value: "B2B" },
                { field: "is_deleted", operator: "eq", value: '0' }
            ];
            post(apiConstants.COURIER_PARTNER_SEARCH, {
                offset: pageNo * limit,
                limit: limit,
                andfilter: andfilter,
            })
                .then((res) => {
                    const data = res?.data?.data.filter(
                        (val) => val.supported_shipment === "B2B"
                    );
                    setTabList(data);
                    setIsLoading(false);
                })
                .catch((err) => {
                    console.log(err);
                    setIsLoading(false);
                });
        }
    }, [sizePerPage, page, filter, reload, queryParams]);

    const handleOpenModal = (rowId, title, prozoId) => {
        setIsUploadModalOpen(true);
        setTitle(title);
        setId(prozoId);
        const rowData = tabList.find((row) => row.id === rowId);
        setRowData(rowData);
    };

    const handleRefreshConfig = (cpProzoId) => {
        setRefConfig(true);
        get(`${apiConstants.CP_REFRESH_CONFIG}?cpProzoId=${cpProzoId}`).then((res) => {
            if (res.status === 200) {
                enqueueSnackbar('Label Fetched Successfully', { variant: 'success' });
            } else {
                enqueueSnackbar('Something went wrong', { variant: 'error' });
            }
        }).finally(() => {
            setRefConfig(false);
        });
    };

    const Columns = [
        {
            field: "parent",
            headerName: "Courier",
            flex: 1.2,
            align: 'left',
            sortable: false,
            renderCell: (params) => (
                <CellStack>
                    <CellLine bold>{params.row?.parent || '—'}</CellLine>
                    {params.row?.cpAccountCode && <CellLine muted ellipsis>{params.row.cpAccountCode}</CellLine>}
                </CellStack>
            ),
        },
        {
            field: "prozoId",
            headerName: "Prozo Courier ID",
            flex: 1,
            align: 'left',
            sortable: false,
            renderCell: (params) => <CellLine muted>{params.row?.prozoId || '—'}</CellLine>,
        },
        {
            field: "email",
            headerName: "Email",
            flex: 1.4,
            align: 'left',
            sortable: false,
            renderCell: (params) => <CellLine muted>{params.row?.email || '—'}</CellLine>,
        },
        {
            field: "pickupRequestEmail",
            headerName: "Pickup Request Email(s)",
            flex: 1.4,
            align: 'left',
            sortable: false,
            renderCell: (params) => <CellLine muted>{params.row?.pickupRequestEmail || '—'}</CellLine>,
        },
        {
            field: "integration",
            headerName: "Pickup Integration",
            flex: 0.8,
            align: 'center',
            renderCell: (params) => {
                const { row } = params;
                if (row.integration && row.integration === 'NONAPI') {
                    return <Chip label="Email" size="small" color="info" variant="outlined" />;
                }
                return <Chip label="N/A" size="small" variant="outlined" />;
            },
        },
        {
            field: "podIntegration",
            headerName: "PoD Via",
            flex: 0.8,
            minWidth: 120,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => params.row?.podIntegration === 'API'
                ? <Chip label="API" size="small" color="primary" variant="outlined" />
                : <Chip label="Manual" size="small" variant="outlined" />,
        },
        {
            field: "b2bPricingRef",
            headerName: "Pricing Uploaded",
            flex: 0.6,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const { row } = params;
                return row.b2bPricingRef
                    ? <Chip label="Yes" size="small" color="success" variant="filled" />
                    : <Chip label="No" size="small" color="default" variant="outlined" />;
            },
        },
        {
            field: "config",
            headerName: "Actions",
            flex: 0.8,
            align: "center",
            headerAlign: 'center',
            sortable: false,
            filterable: false,
            renderCell: (col) => {
                const rowId = col.row.id;
                return (
                    <ActionMenu
                        row={col.row}
                        onEmail={() => handleOpenModal(rowId, "Add Pickup Email", col.row.prozoId)}
                        onKam={() => kamModal("Add KAM Details", col.row.id)}
                        onZone={() =>
                            openModal(
                                "Zone mapping",
                                apiConstants.UPLOAD_COURIER_ZONES,
                                "Upload Zone Mapping",
                                col.row.prozoId,
                                "B2B_ZONE_IDS_MAP",
                                []
                            )
                        }
                        onPricing={() =>
                            openModal(
                                "Courier",
                                apiConstants.UPLOAD_COURIER_PRICING,
                                "Upload Courier Pricing",
                                col.row.prozoId,
                                "B2B_PRICING_TAT",
                                [{ shipmentType: "B2B" }, { type: "COURIER_PRICE_B2B" }]
                            )
                        }
                        onRefresh={() => handleRefreshConfig(col.row.prozoId)}
                        onEdit={() => { setAddOpen(true); setIsEditData(col.row); }}
                    />
                );
            },
        }
    ];

    const openModal = (fileType, url, title, id, sample, others) => {
        setFileType(fileType);
        setUrl(url);
        setTitle(title);
        setId(id);
        setSample(sample);
        setForm(others);
        setIsUploadModalOpen(true);
    };

    const kamModal = (title, id) => {
        setTitle(title);
        setId(id);
        setIsUploadModalOpen(true);
    };

    const closeModal = () => {
        setIsUploadModalOpen(false);
    };

    return (
        <>
            <PageTitle>PTL Courier Partners Master</PageTitle>
            {(isLoading || isRefConfig) && <Loader />}

            <SideDrawer
                drawerWidth='70%'
                title={isEditData ? 'Edit PTL Courier Partner Details' : 'Add PTL Courier Partner Details'}
                subtitle={isEditData ? 'Provide details to edit PTL Courier Partner Details' : 'Provide details to add PTL Courier Partner Details'}
                open={addOpen ? true : false}
                setOpen={() => { setAddOpen(false); setIsEditData(null); }}
                children={
                    <AddCourier
                        supported_shipment={'B2B'}
                        setAddOpen={setAddOpen}
                        setReload={setReload}
                        isEditData={isEditData}
                    />
                }
            />

            <MUIModal
                open={isUploadModalOpen}
                handleClose={closeModal}
                maxWidth='lg'
                title={title}
                children={
                    title === "Add Pickup Email" ? (
                        <AddEmail
                            prozoId={id}
                            closeModal={closeModal}
                            rowData={rowData}
                            setReload={setReload}
                        />
                    ) : title === "Add KAM Details" ? (
                        <AddKAM id={id} />
                    ) : (
                        <CourierUpload
                            id={id}
                            form={form}
                            sample={sample}
                            setReload={setReload}
                            reload={reload}
                            setOpen={isUploadModalOpen}
                            fileType={fileType}
                            url={url}
                        />
                    )
                }
            />

            <Card sx={{ mb: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderRadius: '8px' }}>
                <CardContent>
                    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-end' }}>
                            <FilterField>
                                <FieldLabel>Courier</FieldLabel>
                                <CourierFilter filterList={"B2B"} />
                            </FilterField>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: '0 0 auto' }}>
                                <LoadingButton
                                    loading={isSubmitting}
                                    variant="contained"
                                    size="small"
                                    type="submit"
                                    sx={{ bgcolor: '#1565c0', color: '#fff', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: 2.5, py: 1, '&:hover': { bgcolor: '#0d47a1' } }}
                                >
                                    Filter
                                </LoadingButton>
                                <Button
                                    variant="contained"
                                    size="small"
                                    sx={{ bgcolor: '#ef4444', color: '#fff', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: 2.5, py: 1, '&:hover': { bgcolor: '#dc2626' } }}
                                    onClick={() => {
                                        setCourierFilter(null);
                                        setFilter();
                                        reset({ courier: null });
                                    }}
                                >
                                    Reset
                                </Button>
                            </Box>
                            <Box sx={{ flexGrow: 1 }} />
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() => setAddOpen(true)}
                                sx={{ bgcolor: '#1565c0', color: '#fff', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: 2.5, py: 1, '&:hover': { bgcolor: '#0d47a1' } }}
                            >
                                Add Courier
                            </Button>
                        </Box>
                    </FormProvider>
                </CardContent>
            </Card>

            <Box sx={{ bgcolor: '#fff', borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <ProzoDataGrid
                    columns={Columns}
                    autoHeight={true}
                    rows={courierFilter ? courierFilter : tabList || []}
                    sizePerPage={sizePerPage}
                    setSizePerPage={setSizePerPage}
                    setPage={setPage}
                    page={page}
                    pagination={true}
                    hideFooterPagination={false}
                    getRowHeight={() => 'auto'}
                    rowCount={Number.MAX_VALUE}
                    getRowId={(row) => row.id}
                    sx={gridSx}
                />
            </Box>
        </>
    );
}
